import React, { useMemo } from 'react';
import { Col, Row } from 'antd';
import styled from 'styled-components';

import { WrapperPanel } from 'components';
import { usePointsFormat } from 'hooks';

import { IPointsInfo } from '../../types';
import BDOCustomerCard from '../components/card/BDOCustomerCard';
import {
  applicationTypeOptions,
  CardType,
  civilStatusOptions,
  educationOptions,
  findOptionLabel,
  getCardStatus,
  getCardType,
  incomeLevelOptions,
  membershipTypeOptions,
  residenceTypeOptions
} from '../components/filters/filterOptions';
import BDOCustomerDescription from './BDOCustomerDescription';

export interface ICustomerCardDetails {
  BRC: string | undefined;
  'Allowed To pool': string | undefined;
  'First name': string | undefined;
  CIF: string | undefined;
  'Middle initial': string | undefined;
  Tier: string | undefined;
  'Last name': string | undefined;
  'Card status': string | null;
  'Date of birth': string | undefined;
  'Card sequence no.': string | undefined;
  Gender: string | undefined;
  'Branch name': string | undefined;
  'Home phone': string | undefined;
  'Branch code': string | undefined;
  'Mobile number': string | undefined;
  'Reject reason': string;
  Email: string | undefined;
  'Activation date': string | undefined;
  'Home add': string;
  'Block date': string | undefined;
  'Zip code': string | undefined;
  'Application type': string | undefined;
  'Residence type': string | undefined;
  'Membership type': string | undefined;
  'Civil status': string | undefined;
  'Member since': string | undefined;
  Education: string | undefined;
  'Income level': string | undefined;
}

interface IProp {
  cardInfo: {
    personal_properties?: any;
  };
  pointsInfo: IPointsInfo;
  refetchCustomer: () => void;
  currentTab: 'card' | 'points';
}

const BDOCustomerCardDetails = ({ cardInfo, pointsInfo, refetchCustomer, currentTab }: IProp) => {
  const customerInformation = useMemo(() => {
    const pp = cardInfo?.personal_properties;
    return {
      BRC: pp?.['BDO Rewards Card Number'],
      'Allowed To pool': pp?.['Allowed To pool'],
      'First name': pp?.['Member - First Name'],
      CIF: pp?.['CIF Number'],
      'Middle initial': pp?.['Member -Middle_Initial'],
      Tier: getCardType(pp?.['Card Type'])?.label,
      'Last name': pp?.['Member - Last Name'],
      'Card status': pp?.['Card Status'] ? getCardStatus(pp['Card Status'])?.label : null,
      'Date of birth': pp?.['Birthdate'],
      'Card sequence no.': pp?.['Sequence Number'],
      Gender: pp?.['Gender'],
      'Branch name': pp?.['Branch Name'],
      'Home phone': pp?.['Home - Phone'],
      'Branch code': pp?.['Branch Code'],

      'Mobile number': pp?.['Mobile'],
      'Reject reason': '',
      Email: pp?.['E-Mail'],
      'Activation date': pp?.['Date Approved'],
      'Home add': `${pp?.['Home - Address 1']} ${pp?.['Home - Address 2']} ${pp?.['Home - Address 3']} ${pp?.['Home - Address 4']}`,
      'Block date': pp?.['Block Date'],
      'Zip code': pp?.['Home - Zip Code'],
      'Application type': findOptionLabel(applicationTypeOptions, pp?.['Application Type']),
      'Residence type': findOptionLabel(residenceTypeOptions, pp?.['Residence Type']),
      'Membership type': findOptionLabel(membershipTypeOptions, pp?.['Membership Type']),
      'Civil status': findOptionLabel(civilStatusOptions, pp?.['Civil Status']),
      'Member since': pp?.['MemberSince'],
      Education: findOptionLabel(educationOptions, pp?.['Education']), // FIXME: fix display education
      'Income level': findOptionLabel(incomeLevelOptions, pp?.['Income Level'])
    } as ICustomerCardDetails;
  }, [cardInfo]);

  const isPool = customerInformation?.['Allowed To pool'] === 'N' ? true : false; //if its N then its a virtual card

  const { formatPoints } = usePointsFormat();

  return (
    <WrapperPanel title={`${currentTab.toUpperCase()} INFORMATION`} data-testid="customer-details">
      <Row>
        <Col span={6} style={{ marginRight: '6rem' }}>
          <BDOCustomerCard
            type={
              getCardType(cardInfo?.personal_properties?.['Card Type'])?.label as CardType['label']
            }
            status={cardInfo?.personal_properties?.['Card Status']}
            refetchCustomer={refetchCustomer}
            cardProps={{
              brc: customerInformation['BRC'],
              tier: cardInfo?.personal_properties?.['Card Type'],
              sequence: customerInformation['Card sequence no.'],
              activity_description: cardInfo?.personal_properties?.['Activity Description']
            }}
            isPool={isPool}
          />
        </Col>
        <Col span={16}>
          {currentTab === 'card' && (
            <BDOCustomerDescription customerInformation={customerInformation} />
          )}
          {currentTab === 'points' && (
            <GrayBox>
              <Item>
                <div>Available points</div>
                <div>
                  {pointsInfo.pb !== null && pointsInfo.pb !== undefined
                    ? formatPoints(pointsInfo.pb)
                    : '-'}
                </div>
              </Item>
              <Item>
                <div>Total earned points</div>
                <div>
                  {pointsInfo.lpb !== null && pointsInfo.lpb !== undefined
                    ? formatPoints(pointsInfo.lpb)
                    : '-'}
                </div>
              </Item>
              <Item>
                <div>Total redeemed points</div>
                <div>
                  {pointsInfo.trp !== null && pointsInfo.trp !== undefined
                    ? formatPoints(pointsInfo.trp)
                    : '-'}
                </div>
              </Item>
              <Item>
                <div>Total points transfer (+)</div>
                <div>
                  {pointsInfo.tpr !== null && pointsInfo.tpr !== undefined
                    ? formatPoints(pointsInfo.tpr)
                    : '-'}
                </div>
              </Item>
              <Item>
                <div>Total points transfer (-)</div>
                <div>
                  {pointsInfo.tpt !== null && pointsInfo.tpt !== undefined
                    ? formatPoints(pointsInfo.tpt)
                    : '-'}
                </div>
              </Item>
            </GrayBox>
          )}
        </Col>
      </Row>
    </WrapperPanel>
  );
};

export const GrayBox = styled.div`
  padding: 1rem;
  border: 1px solid #d8d8d8;
  border-radius: 4px;
  background-color: #fafafa;
  width: 240px;
  font-size: 14px;
  line-height: 26px;
`;

const Item = styled.div`
  display: flex;
  justify-content: space-between;
`;

export default BDOCustomerCardDetails;
